import { TEMPLATE_STATUSES } from '@constants/statuses';
import type { PublishingOptionType } from '@ContractBuilder/types';
import type { BrandingStatus } from '@domain/schemas/branding.schema';
import { ResourceState } from '@domain/schemas/contract.schema';
import StatusBadge from '@pages/User/components/StatusBadge';
import { createdAtAccessor, lastUpdatedByAccessor, updatedAtAccessor } from '@pages/User/helpers/columns/shared';
import { getFormattedStatus } from '@root/helpers/resources';
import { type CellContext, createColumnHelper } from '@tanstack/react-table';

import type { Branding } from './types';

const columnHelper = createColumnHelper<Branding>();

export const INITIAL_BRANDING_COLS = [
  columnHelper.accessor('id', {
    id: 'id',
    enableColumnFilter: false,
    enableHiding: false,
  }),
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Branding name',
    sortingFn: 'text',
    sortUndefined: -1,
    filterFn: 'arrIncludesSome',
    enableHiding: false,
    meta: {
      filter: {
        placeholder: 'All branding',
      },
      tooltip: true,
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    filterFn: 'arrIncludesSome',
    cell: (cellContext: CellContext<Branding, string>) => <StatusBadge status={cellContext.getValue()} />,
    meta: {
      filter: {
        placeholder: 'All statuses',
        selected: TEMPLATE_STATUSES.filter((status) => status !== 'ARCHIVED'),
        options: TEMPLATE_STATUSES.map((status) => ({
          name: getFormattedStatus(status),
          value: status,
        })),
      },
      dataCellClassName: 'px-3 py-1',
    },
  }),
  createdAtAccessor(columnHelper),
  updatedAtAccessor(columnHelper),
  columnHelper.accessor('created_by', {
    id: 'created_by',
    header: 'Created by',
    filterFn: 'arrIncludesSome',
    meta: {
      filter: {
        placeholder: 'All created by',
      },
      tooltip: true,
    },
  }),
  lastUpdatedByAccessor(columnHelper),
  columnHelper.display({
    id: 'view',
    enableColumnFilter: false,
    header: '',
    enableHiding: false,
    size: 65,
    minSize: 65,
    maxSize: 65,
    cell: (cellContext: CellContext<Branding, unknown>) => (
      <a
        className="text-primary-600 hover:text-primary-900 font-light"
        target="_self"
        href={`/branding/${cellContext.row.getValue('id')}`}
        rel="noreferrer"
      >
        View
      </a>
    ),
    meta: {
      dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
    },
  }),
];

export const brandingPublishingOptions: PublishingOptionType[] = [
  {
    id: ResourceState.DRAFT,
    title: 'Draft',
    description: 'This branding is a work in progress and unavailable to use on new submissions.',
  },
  {
    id: ResourceState.PUBLISHED,
    title: 'Published',
    description: 'Make this branding available to use when creating new submissions.',
  },
  {
    id: ResourceState.ARCHIVED,
    title: 'Archived',
    description: 'Prevent this branding from being used when creating new submissions.',
  },
];

export const ALLOWED_STATUSES: Record<BrandingStatus, BrandingStatus[]> = {
  DRAFT: [ResourceState.ARCHIVED, ResourceState.PUBLISHED, ResourceState.DRAFT],
  PUBLISHED: [ResourceState.ARCHIVED, ResourceState.PUBLISHED],
  ARCHIVED: [ResourceState.ARCHIVED, ResourceState.PUBLISHED],
};

export const IMG_VALID_TIME = 300 * 1000;

export const ERROR_BRANDING_NOT_FOUND = 'Branding not found';
